import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { mdiCheckCircleOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Zoom } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useContainer } from 'unstated-next';
import * as yup from 'yup';

import PasswordResetContainer from './PasswordResetContainer';
import {
    ButtonText,
    FormContainer,
    FormFields,
    HelperText,
    ResetButton,
    StyledFormControl
} from './styles/password-reset';
import { ControlledInput } from '../../components/forms';
import { getUrlParam } from '../../utils/url';

export interface PasswordResetFormProps {}

const PasswordResetForm: React.FC<PasswordResetFormProps> = () => {
    const { t } = useTranslation('password_reset');
    const { isLoading, isFinished, resetPassword } = useContainer(PasswordResetContainer);

    const schema = yup.object({
        password: yup
            .string()
            .required(t('errors.password-required'))
            .min(10, t('errors.password-min'))
            .matches(/.*[0-9].*/, t('errors.password-num'))
            .matches(/.*[a-zA-Z].*/, t('errors.password-alpha')),
        'password-confirm': yup.string().oneOf([yup.ref('password'), null], t('errors.password-match'))
    });

    const { control, handleSubmit } = useForm({ resolver: yupResolver(schema) });

    const onSubmit = (data) => {
        const token = getUrlParam('token');

        resetPassword(data.password, token);
    };

    const getButtonText = () => {
        if (isFinished) {
            return (
                <ButtonText>
                    {t('password-updated')} <Icon path={mdiCheckCircleOutline} />
                </ButtonText>
            );
        }

        return t('update-password');
    };

    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormFields>
                    <StyledFormControl fullWidth>
                        <ControlledInput type="password" name="password" label={t('password')} control={control} />
                    </StyledFormControl>
                    <StyledFormControl fullWidth>
                        <ControlledInput
                            type="password"
                            name="password-confirm"
                            label={t('confirm-password')}
                            control={control}
                        />
                    </StyledFormControl>
                </FormFields>
                <ResetButton fullWidth variant={'contained'} type={'submit'} loading={isLoading} disabled={isFinished}>
                    {getButtonText()}
                </ResetButton>
            </form>
            <Zoom in={isFinished}>
                <HelperText>
                    <Trans i18nKey={'password_reset:your-password-been-updated'}>
                        Your password has been updated. Please <Link to={'/login'}>sign in</Link> again.
                    </Trans>
                </HelperText>
            </Zoom>
        </FormContainer>
    );
};

export default PasswordResetForm;
